import React, { useState } from 'react';
import { Typography } from '../../../interfaces/typography';
import { AgentType, PartyRole, Platform, PLATFORM_TO_HUMAN_READABLE } from '../../../interfaces/db';
import { X, Share2, Phone, ExternalLink, FileText } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { SessionDetailView, viewHasMinimalHeader } from '../../History/views';
import { SessionStatus } from '../../SessionStatus/SessionStatus';
import { useUserData } from '../../../lib/redux/users/user-data-hook';
import { SessionDataResult } from 'interfaces/sessions';
import { PROSPECT_INFO_COLUMNS } from 'interfaces/table';

interface TranscriptHeaderProps {
  data: SessionDataResult;
  onClose?: () => void;
  onCopyTranscript?: () => void;
  view: SessionDetailView;
}

export const TranscriptHeader: React.FC<TranscriptHeaderProps> = ({
  data,
  onClose,
  view
}) => {
  const [showCopied, setShowCopied] = useState(false);
  const [showPhoneCopied, setShowPhoneCopied] = useState(false);
  const [showTranscriptCopied, setShowTranscriptCopied] = useState(false);

  // Get the current user and visible accounts from hooks
  const { user, allVisibleUsers } = useUserData();

  const formatPhoneNumber = (phoneNumber: string) => {
    if (phoneNumber.charAt(0) === '1') {
      return phoneNumber.replace(/(\d{1})(\d{3})(\d{3})(\d{4})/, '+$1 ($2) $3-$4');
    }
    return phoneNumber.replace(/(\d{1,4})(\d{2,4})(\d{4})/, '+$1 $2 $3');
  };

  const getTitle = () => {
    let title = data.prospect_info?.prospect_title;
    const platform = data.prospect_info?.platform;

    if (platform === Platform.SALESLOFT) {
      title = `${data.prospect_info?.prospect_seniority ?? ''} ${data.prospect_info?.prospect_title ?? ''}`.trim();
    } else if (platform === Platform.OUTREACH) {
      title = data.prospect_info?.prospect_title || data.prospect_info?.prospect_occupation;
    }

    return title?.trim() || null;
  };

  const getPlatform = () => {
    return data.prospect_info?.[PROSPECT_INFO_COLUMNS.PLATFORM]
  }

  const getCompanyName = () => {
    return data.prospect_info?.[PROSPECT_INFO_COLUMNS.COMPANY_NAME]
  }

  const getSessionUserId = () => {
    if (!data.session_agents) return null
    if (data.session_agents.creator_agent_type !== AgentType.USER) return null
    return data.session_agents.creator_agent_id
  }

  const getRepNumber = () => {
    const agentAddress = data.session.agent_address;
    return agentAddress && agentAddress !== '-' ? agentAddress : null;
  };

  const constructPlatformLink = (platform: Platform, personId: string | null): string | null => {
    if (!personId) return null;
    if (platform === Platform.SALESLOFT) {
      return `https://app.salesloft.com/app/people/${personId}`;
    } else if (platform === Platform.OUTREACH) {
      return `https://web.outreach.io/prospects/${personId}/overview`;
    } 
    return null;
  }

  const getPlatformUrl = (): string | null => {
    if (!data.prospect_info) return null;
    
    const platform = data.prospect_info[PROSPECT_INFO_COLUMNS.PLATFORM];
    const personId = data.prospect_info[PROSPECT_INFO_COLUMNS.PLATFORM_PERSON_ID] ?? data.prospect_info[PROSPECT_INFO_COLUMNS.XREF_PERSON_ID];
    const platformLink = data.prospect_info[PROSPECT_INFO_COLUMNS.PLATFORM_PERSON_URL] ?? data.prospect_info[PROSPECT_INFO_COLUMNS.XREF_PERSON_URL];
    if (!platform || (!personId && !platformLink)) return null;
    return platformLink ?? constructPlatformLink(platform, personId);
  }

  const handleCopyLink = async () => {
    try {
      const url = `https://${window.location.hostname}/transcripts?id=${data.session.session_id}`;
      await navigator.clipboard.writeText(url);
      setShowCopied(true);
      setTimeout(() => setShowCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
    }
  };

  const handleCopyPhone = async () => {
    if (!data.session.customer_address) return;
    try {
      await navigator.clipboard.writeText(data.session.customer_address);
      setShowPhoneCopied(true);
      setTimeout(() => setShowPhoneCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy phone number:', err);
    }
  };

  const handleCopyTranscript = async () => {
    try {
      // Format each turn into text
      const transcriptText = data.turns
        .filter(turn => turn.text.trim() !== '') // Skip empty turns
        .map(turn => {
          const speaker = turn.role === PartyRole.REP ? 'Rep' : 'Prospect';
          return `${speaker}: ${turn.text.trim()}`;
        })
        .join('\n');

      // Add header with call details
      const headerParts = [
        `Call with ${'Unknown Prospect'}`,
        `Date: ${data.session.started_at.toLocaleString('en-US', { 
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
          hour12: true
        })}`,
      ];

      if (data.prospect_info?.[PROSPECT_INFO_COLUMNS.COMPANY_NAME]) {
        headerParts.push(`Company: ${data.prospect_info[PROSPECT_INFO_COLUMNS.COMPANY_NAME]}`);
      }

      const fullTranscript = `${headerParts.join('\n')}\n\n${transcriptText}`;
      
      await navigator.clipboard.writeText(fullTranscript);
      setShowTranscriptCopied(true);
      setTimeout(() => setShowTranscriptCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy transcript:', err);
    }
  };

  const getDateFormat = (): Intl.DateTimeFormatOptions => {
    if (viewHasMinimalHeader(view)) {
      return { month: 'short', day: 'numeric' }
    }
    return { month: 'long', day: 'numeric', hour: 'numeric', minute: 'numeric', hour12: true }
  }

  const sessionUserId = getSessionUserId()
  const companyName = getCompanyName()
  const title = getTitle()
  const platform = getPlatform()
  const repNumber = getRepNumber()

  return (
    <div className="w-full border-b border-gray-100">
      <div className="flex flex-col p-2">
        {/* Name, title, date, and action buttons */}
        <div className="flex justify-between items-start">
          <div className="flex items-center flex-wrap gap-x-2 gap-y-0.5 max-w-[75%]">
            <Typography variant="h1" className="font-semibold truncate">
              {data.prospect_info?.[PROSPECT_INFO_COLUMNS.PROSPECT_NAME] ?? 'Unknown Prospect'}
            </Typography>
            
            {title && (
              <div className="flex items-center max-w-[300px] min-w-0">
                <Typography variant="mediumParagraph" color="gray" className="truncate w-full">
                  {title}
                </Typography>
              </div>
            )}
            
            <div className="flex items-center gap-2">
              <Typography variant="mediumParagraph" color="gray">
                {data.session.started_at.toLocaleString('en-US', getDateFormat())}
              </Typography>
            </div>
          </div>
          
          {/* Action buttons */}
          <div className="flex items-center gap-2">
            {/* Session status */}
            {user && sessionUserId && (
              <div className="flex items-center">
                <SessionStatus 
                  isMainView={true} 
                  sessionId={data.session.session_id} 
                  sessionUserId={sessionUserId} 
                  type={'sessionData'} 
                  viewerUserId={user.user_id} 
                  reviewIsOpen={data.review_status?.is_open}
                  reviewOpenedByUser={data.review_status?.opening_user_id === user.user_id}
                />
              </div>
            )}

            {/* Copy transcript button */}
            {(
              <div className="relative">
                <button 
                  onClick={handleCopyTranscript}
                  className="p-1.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                  aria-label="Copy transcript"
                >
                  <FileText size={16} />
                </button>
                
                {/* Copy notification */}
                {/* @ts-ignore - Ignoring the AnimatePresence type error as it's a known issue with framer-motion types */}
                <AnimatePresence>
                  {showTranscriptCopied && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      className="absolute top-full right-0 mt-1 px-2 py-1 
                        bg-gray-800 text-white text-xs rounded whitespace-nowrap z-50"
                    >
                      <Typography variant="smallParagraph" color="white">
                        Transcript copied!
                      </Typography>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
            
            {/* Share button */}
            <div className="relative">
              <button 
                onClick={(view === SessionDetailView.MAIN) ? handleCopyLink : () => {
                  window.open(`https://${window.location.hostname}/transcripts?id=${data.session.session_id}`, '_blank');
                }}
                className="p-1.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                aria-label="Share transcript"
              >
                <Share2 size={16} />
              </button>
              
              {/* Copy notification */}
              {/* @ts-ignore - Ignoring the AnimatePresence type error as it's a known issue with framer-motion types */}
              <AnimatePresence>
                {showCopied && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="absolute top-full right-0 mt-1 px-2 py-1 
                      bg-gray-800 text-white text-xs rounded whitespace-nowrap z-50"
                  >
                    <Typography variant="smallParagraph" color="white">
                      URL copied!
                    </Typography>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            
            {/* Close button */}
            {onClose && (
              <button 
                onClick={onClose}
                className="p-1.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                aria-label="Close transcript"
              >
                <X size={16} />
              </button>
            )}
          </div>
        </div>

        {/* Group all non-name rows together */}
        <div className="flex flex-col gap-1.5">
          {/* Contact info, company, platform link */}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            {/* Phone number with copy functionality */}
            {data.session.customer_address && (
              <div className="relative">
                <button 
                  onClick={handleCopyPhone}
                  className="flex items-center gap-1 hover:bg-gray-50 rounded px-1 py-0.5 transition-colors group"
                >
                  <Phone size={14} className="text-gray-500" />
                  <Typography variant="mediumParagraph">
                    {formatPhoneNumber(data.session.customer_address)}
                  </Typography>
                </button>
                
                {/* Copy notification */}
                {/* @ts-ignore - Ignoring the AnimatePresence type error as it's a known issue with framer-motion types */}
                <AnimatePresence>
                  {showPhoneCopied && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      className="absolute top-full left-0 mt-1 px-2 py-1 
                        bg-gray-800 text-white text-xs rounded whitespace-nowrap z-50"
                    >
                      <Typography variant="smallParagraph" color="white">
                        Copied!
                      </Typography>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
            
            {/* Rep number */}
            {repNumber && (
              <div className="flex items-center gap-1">
                <Typography variant="mediumParagraph" color="gray">
                  Called from:
                </Typography>
                <Typography variant="mediumParagraph">
                  {formatPhoneNumber(repNumber)}
                </Typography>
              </div>
            )}
            
            {/* Company name */}
            {companyName && (
              <div className="flex items-center gap-1">
                <Typography variant="mediumParagraph" color="gray">
                  Company:
                </Typography>
                <Typography variant="mediumParagraph">
                  {companyName}
                </Typography>
              </div>
            )}
            
            {/* Platform - make clickable if URL exists */}
            {platform && <div className="flex items-center gap-1">
              <Typography variant="mediumParagraph" color="gray">
                Platform:
              </Typography>
              {getPlatformUrl() ? (
                <a 
                  href={getPlatformUrl() || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 transition-colors"
                >
                  <Typography variant="mediumParagraph">
                    {PLATFORM_TO_HUMAN_READABLE[platform]}
                  </Typography>
                  <ExternalLink size={14} />
                </a>
              ) : (
                <Typography variant="mediumParagraph">
                  {PLATFORM_TO_HUMAN_READABLE[platform]}
                </Typography>
              )}
            </div>}

            {/* Show user who placed the call if different from viewer */}
            {user && data.sales_metric?.user_id !== user.user_id && (
              <div className="flex items-center gap-1">
                <Typography variant="mediumParagraph" color="gray">
                  Rep:
                </Typography>
                <Typography variant="mediumParagraph">
                  {allVisibleUsers.find((u) => u.user_id === getSessionUserId())?.user_name ?? "Deactivated User"}
                </Typography>
              </div>
            )}
          </div>
          
          {/* Disposition, sentiment and previous calls on their own row - always show this row for previous calls */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Previous calls indicator - always show first */}
            {data.sales_metric?.user_disposition && (
              <div className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-gray-50 rounded border border-gray-100">
                <Typography variant="mediumParagraph" color="gray">
                  Disposition:
                </Typography>
                <Typography variant="mediumParagraph">
                  {data.sales_metric.user_disposition.charAt(0).toUpperCase() + 
                   data.sales_metric.user_disposition.slice(1).toLowerCase()}
                </Typography>
              </div>
            )}
            
            {/* Sentiment */}
            {data.sales_metric?.user_sentiment && (
              <div className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-gray-50 rounded border border-gray-100">
                <Typography variant="mediumParagraph" color="gray">
                  Sentiment:
                </Typography>
                <Typography variant="mediumParagraph">
                  {data.sales_metric.user_sentiment.charAt(0).toUpperCase() + 
                   data.sales_metric.user_sentiment.slice(1).toLowerCase()}
                </Typography>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}; 